// src/components/LoginPage.jsx — Nuvport OS auth page
// Split layout: 40% brand left, 60% form right. All 4 auth states.
import { useState, useEffect, useCallback, useRef } from "react";

// ── PALETTE ─────────────────────────────────────────────────────────────────
const BG     = "#0a0a0a";
const BG2    = "#111111";
const BORDER = "#1c1c1c";
const BDR2   = "#242424";
const GREEN  = "#3ddc84";
const GDIM   = "#2db870";
const RED    = "#FF4455";
const TEXT   = "#f0f0f0";
const MUTED  = "#888888";
const FAINT  = "#0E180F";
const AMBER  = "#FFB800";

const MONO    = "'IBM Plex Mono', 'Courier New', monospace";
const SANS    = "'IBM Plex Sans', sans-serif";
const DISPLAY = "'Playfair Display', Georgia, serif";

// ── LEFT PANEL CONTENT ───────────────────────────────────────────────────────
const FEATURES = [
  "16 institutional-grade modules",
  "Live geo-risk intelligence map",
  "$29/month — not $2,000",
];

const ROTATING_STATS = [
  "The only retail terminal with 30+ live geopolitical intelligence layers.",
  "19 active conflict zones mapped to your portfolio exposure.",
  "Bloomberg intelligence. Not Bloomberg prices.",
  "2-second live data refresh across all 16 modules simultaneously.",
];

export default function LoginPage({
  onBack, signIn, signUp, signInWithGoogle,
  authError, clearError, hasSupabase,
  onOpenTerms, onOpenPrivacy,
  resetPassword, updatePassword, isPasswordRecovery,
}) {
  const [mode,            setMode]            = useState("signin");
  const [email,           setEmail]           = useState("");
  const [password,        setPassword]        = useState("");
  const [confirm,         setConfirm]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass,        setShowPass]        = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [success,         setSuccess]         = useState("");
  const [termsChecked,    setTermsChecked]    = useState(false);
  const [cursor,          setCursor]          = useState({ x: -100, y: -100, big: false });
  const [statIdx,         setStatIdx]         = useState(0);
  const statTimer = useRef(null);

  // Rotate bottom stat every 4s
  useEffect(() => {
    statTimer.current = setInterval(() => {
      setStatIdx(i => (i + 1) % ROTATING_STATS.length);
    }, 4000);
    return () => clearInterval(statTimer.current);
  }, []);

  const switchMode = (m) => {
    setMode(m);
    setSuccess("");
    clearError();
    setTermsChecked(false);
    setShowPass(false);
    setShowConfirm(false);
  };

  useEffect(() => {
    if (isPasswordRecovery) setMode("update_password");
  }, [isPasswordRecovery]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    clearError();
    try {
      if (mode === "update_password") {
        if (password !== confirmPassword) { setLoading(false); return; }
        const ok = await updatePassword(password);
        if (ok) setSuccess("Password updated. You are now signed in.");
      } else if (mode === "register") {
        if (password !== confirm) { setLoading(false); return; }
        localStorage.setItem("terms_pending_acceptance", new Date().toISOString());
        const ok = await signUp(email, password);
        if (ok) setSuccess("Account created. Check your email to confirm, then sign in.");
        else localStorage.removeItem("terms_pending_acceptance");
      } else if (mode === "reset") {
        const ok = await resetPassword(email);
        if (ok) setSuccess("Check your email for a reset link.");
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      console.warn('[LoginPage] handleSubmit error:', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Custom cursor
  useEffect(() => {
    const onMove = (e) => setCursor(c => ({ ...c, x: e.clientX, y: e.clientY }));
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);
  const makeBig   = useCallback(() => setCursor(c => ({ ...c, big: true  })), []);
  const makeSmall = useCallback(() => setCursor(c => ({ ...c, big: false })), []);

  const modeLabel = {
    signin:          "SIGN IN",
    register:        "CREATE ACCOUNT",
    reset:           "RESET PASSWORD",
    update_password: "SET NEW PASSWORD",
  };
  const modeHeading = {
    signin:          "Welcome back.",
    register:        "Create your account.",
    reset:           "Reset password.",
    update_password: "Set new password.",
  };

  const CSS = `
    @keyframes lp-blink   { 0%,100%{opacity:1} 50%{opacity:0} }
    @keyframes lp-slideIn { from{opacity:0;transform:translateX(-14px)} to{opacity:1;transform:none} }
    @keyframes lp-fadeStat{ 0%{opacity:0;transform:translateY(6px)} 15%,85%{opacity:1;transform:none} 100%{opacity:0;transform:translateY(-6px)} }
    .lp-scanlines { position:fixed;inset:0;z-index:1000;pointer-events:none;
      background:repeating-linear-gradient(to bottom,transparent 0,transparent 2px,rgba(0,0,0,.07) 2px,rgba(0,0,0,.07) 4px); }
    .lp-noise { position:fixed;inset:0;z-index:999;pointer-events:none;opacity:.025;
      background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
      background-size:256px; }
    .lp-blink-dot { animation:lp-blink 1.4s ease infinite; }
    .lp-stat-text { animation:lp-fadeStat 4s ease-in-out; }

    /* Inputs */
    .lp-input { width:100%;box-sizing:border-box;background:${BG2};border:1px solid ${BDR2};
      color:${TEXT};font-size:12px;padding:12px 16px;font-family:${MONO};outline:none;
      transition:border-color .15s,box-shadow .15s;letter-spacing:.04em; }
    .lp-input:focus { border-color:rgba(61,220,132,.5);box-shadow:0 0 0 1px rgba(61,220,132,.12); }
    .lp-input:disabled { opacity:.4; }
    .lp-input-wrap { position:relative; }
    .lp-input-wrap .lp-input { padding-right:44px; }
    .lp-eye { position:absolute;right:14px;top:50%;transform:translateY(-50%);
      background:none;border:none;color:${MUTED};cursor:none;padding:0;font-size:12px;
      transition:color .15s;line-height:1; }
    .lp-eye:hover { color:${TEXT}; }

    /* Buttons */
    .lp-btn-primary { font-family:${DISPLAY};font-size:13px;letter-spacing:.14em;color:${BG};
      background:${GREEN};border:none;padding:14px 0;width:100%;cursor:none;
      clip-path:polygon(0 0,calc(100% - 8px) 0,100% 8px,100% 100%,8px 100%,0 calc(100% - 8px));
      transition:background .15s,box-shadow .15s; }
    .lp-btn-primary:hover:not(:disabled) { background:${GDIM};box-shadow:0 0 40px rgba(61,220,132,.3); }
    .lp-btn-primary:disabled { opacity:.45;cursor:not-allowed; }

    .lp-btn-google { font-family:${MONO};font-size:10px;letter-spacing:.1em;color:${TEXT};
      background:${BG2};border:1px solid ${BDR2};padding:12px 0;width:100%;cursor:none;
      display:flex;align-items:center;justify-content:center;gap:10px;
      transition:border-color .15s,box-shadow .15s; }
    .lp-btn-google:hover:not(:disabled) { border-color:${GREEN};box-shadow:0 0 0 1px rgba(61,220,132,.15); }
    .lp-btn-google:disabled { opacity:.4;cursor:not-allowed; }

    .lp-tb-back { background:transparent;border:none;color:${MUTED};font-size:10px;
      letter-spacing:.08em;cursor:none;font-family:${MONO};
      display:inline-flex;align-items:center;gap:6px;transition:color .15s;padding:0; }
    .lp-tb-back:hover { color:${GREEN}; }

    .lp-link { background:none;border:none;color:${MUTED};font-family:${MONO};
      font-size:10px;letter-spacing:.06em;cursor:none;padding:0;
      transition:color .15s;text-align:left; }
    .lp-link:hover { color:${TEXT}; }
    .lp-link-green { color:${GREEN}; }
    .lp-link-green:hover { color:#fff; }

    /* Mobile */
    @media(max-width:800px) {
      .lp-left  { display:none!important; }
      .lp-right { width:100%!important; }
    }
  `;

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: MONO, color: TEXT, display: "flex", flexDirection: "column", cursor: "none", position: "relative" }}>
      <style>{CSS}</style>
      <div className="lp-scanlines" />
      <div className="lp-noise" />

      {/* Custom cursor */}
      <div style={{
        position: "fixed", zIndex: 9999, pointerEvents: "none",
        left: cursor.x, top: cursor.y,
        width:  cursor.big ? 28 : 4,
        height: cursor.big ? 28 : 18,
        borderRadius: cursor.big ? "50%" : 0,
        background:   cursor.big ? "transparent" : GREEN,
        border:       cursor.big ? `1px solid ${GREEN}` : "none",
        transform: "translate(-50%,-50%)",
        boxShadow: cursor.big ? "0 0 24px rgba(61,220,132,.25), inset 0 0 10px rgba(61,220,132,.05)" : `0 0 14px ${GREEN}`,
        transition: "width .2s, height .2s, border-radius .2s",
      }} />

      {/* ── TOP BAR ─────────────────────────────────────────────────────── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        height: 44,
        background: "rgba(10,10,10,.95)", backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${BDR2}`,
        display: "flex", alignItems: "center", padding: "0 32px", gap: 10,
      }}>
        <span className="lp-blink-dot" style={{ width: 6, height: 6, borderRadius: "50%", background: GREEN, boxShadow: `0 0 8px ${GREEN}`, flexShrink: 0, display: "inline-block" }} />
        <span style={{ fontFamily: DISPLAY, fontSize: 15, letterSpacing: ".14em", color: GREEN, textShadow: `0 0 16px rgba(61,220,132,.4)` }}>
          NUVPORT OS
        </span>
        <div style={{ flex: 1 }} />
        <button className="lp-tb-back" onClick={onBack} onMouseEnter={makeBig} onMouseLeave={makeSmall}>
          ← Back to Nuvport OS
        </button>
      </div>

      {/* ── MAIN ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", alignItems: "stretch", position: "relative", zIndex: 1 }}>

        {/* ── LEFT — 40% branding panel ─────────────────────────────────── */}
        <div className="lp-left" style={{
          width: "40%", flexShrink: 0,
          background: BG,
          borderRight: `1px solid ${BDR2}`,
          padding: "80px 56px",
          display: "flex", flexDirection: "column", justifyContent: "center",
          position: "relative", overflow: "hidden",
        }}>
          {/* Grid background */}
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage: `linear-gradient(${BORDER} 1px,transparent 1px),linear-gradient(90deg,${BORDER} 1px,transparent 1px)`,
            backgroundSize: "60px 60px",
            WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 30% 40%,black 0%,transparent 70%)",
            maskImage: "radial-gradient(ellipse 80% 60% at 30% 40%,black 0%,transparent 70%)",
            opacity: 0.5, pointerEvents: "none",
          }} />
          {/* Ambient glow */}
          <div style={{ position: "absolute", top: "40%", left: "30%", transform: "translate(-50%,-50%)", width: 400, height: 300, background: "radial-gradient(ellipse,rgba(61,220,132,.05) 0%,transparent 70%)", pointerEvents: "none" }} />

          {/* Eyebrow */}
          <div style={{ fontSize: 9, letterSpacing: ".22em", textTransform: "uppercase", color: GREEN, marginBottom: 20, animation: "lp-slideIn .5s ease both", position: "relative", zIndex: 1 }}>
            // Investment Terminal
          </div>

          {/* Logo */}
          <div style={{ fontFamily: DISPLAY, fontSize: "clamp(40px,5vw,68px)", lineHeight: 0.95, letterSpacing: ".04em", marginBottom: 16, animation: "lp-slideIn .5s .06s ease both", position: "relative", zIndex: 1 }}>
            <span style={{ color: GREEN, textShadow: "0 0 40px rgba(61,220,132,.3)" }}>NUVPORT</span>
            <br />
            <span style={{ color: TEXT }}>OS</span>
          </div>

          {/* Tagline */}
          <p style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".08em", color: MUTED, lineHeight: 1.7, marginBottom: 44, animation: "lp-slideIn .5s .12s ease both", position: "relative", zIndex: 1 }}>
            Bloomberg intelligence.<br />
            <span style={{ color: TEXT }}>Not Bloomberg prices.</span>
          </p>

          {/* Feature bullets with ✓ */}
          <div style={{ animation: "lp-slideIn .5s .18s ease both", position: "relative", zIndex: 1, marginBottom: "auto" }}>
            {FEATURES.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                <span style={{ color: GREEN, fontFamily: MONO, fontSize: 13, lineHeight: 1, textShadow: `0 0 8px rgba(61,220,132,.5)`, flexShrink: 0, paddingTop: 1 }}>✓</span>
                <span style={{ fontFamily: SANS, fontSize: 13, color: TEXT, fontWeight: 300, lineHeight: 1.5 }}>{item}</span>
              </div>
            ))}
          </div>

          {/* Rotating stat at bottom */}
          <div style={{ marginTop: 56, position: "relative", zIndex: 1, minHeight: 52 }}>
            <div style={{ height: 1, background: `linear-gradient(90deg,${GREEN}44,transparent)`, marginBottom: 16 }} />
            <p key={statIdx} className="lp-stat-text" style={{ fontFamily: SANS, fontSize: 12, color: MUTED, lineHeight: 1.7, fontWeight: 300, margin: 0 }}>
              "{ROTATING_STATS[statIdx]}"
            </p>
          </div>
        </div>

        {/* ── RIGHT — 60% auth form ──────────────────────────────────────── */}
        <div className="lp-right" style={{
          flex: 1,
          background: BG2,
          padding: "0 48px",
          display: "flex", flexDirection: "column", justifyContent: "center",
          maxWidth: 520, margin: "0 auto",
        }}>

          {/* Section label */}
          <div style={{ fontSize: 9, letterSpacing: ".2em", textTransform: "uppercase", color: GREEN, marginBottom: 12 }}>
            // {modeLabel[mode]}
          </div>

          {/* Heading */}
          <h1 style={{ fontFamily: DISPLAY, fontSize: "clamp(28px,4vw,44px)", lineHeight: 1.0, letterSpacing: ".03em", color: "#fff", margin: "0 0 32px" }}>
            {modeHeading[mode]}
          </h1>

          {/* Supabase warning */}
          {!hasSupabase && (
            <div style={{ background: "#1a1000", border: `1px solid ${AMBER}33`, padding: "12px 16px", marginBottom: 24, fontSize: 10, color: AMBER, lineHeight: 1.7 }}>
              Supabase not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env to enable auth.
            </div>
          )}

          {/* ── UPDATE PASSWORD FORM ── */}
          {mode === "update_password" && (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label style={{ display: "block", fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>NEW PASSWORD</label>
                <div className="lp-input-wrap">
                  <input type={showPass ? "text" : "password"} required autoComplete="new-password"
                    className="lp-input" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••" disabled={!hasSupabase || !!success}
                    onMouseEnter={makeBig} onMouseLeave={makeSmall} />
                  <button type="button" className="lp-eye" onClick={() => setShowPass(v => !v)}
                    onMouseEnter={makeBig} onMouseLeave={makeSmall} tabIndex={-1}>
                    {showPass ? "●" : "○"}
                  </button>
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>CONFIRM NEW PASSWORD</label>
                <div className="lp-input-wrap">
                  <input type={showConfirm ? "text" : "password"} required autoComplete="new-password"
                    className="lp-input" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="••••••••" disabled={!hasSupabase || !!success}
                    style={{ borderColor: confirmPassword && password !== confirmPassword ? RED : BDR2 }}
                    onMouseEnter={makeBig} onMouseLeave={makeSmall} />
                  <button type="button" className="lp-eye" onClick={() => setShowConfirm(v => !v)}
                    onMouseEnter={makeBig} onMouseLeave={makeSmall} tabIndex={-1}>
                    {showConfirm ? "●" : "○"}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <div style={{ color: RED, fontSize: 9, marginTop: 6, letterSpacing: ".06em" }}>Passwords do not match</div>
                )}
              </div>
              {authError && <div style={{ color: RED, fontSize: 10, background: "#1a0800", border: `1px solid ${RED}22`, padding: "10px 14px", lineHeight: 1.6 }}>{authError}</div>}
              {success  && <div style={{ color: GREEN, fontSize: 10, background: FAINT, border: `1px solid ${GREEN}22`, padding: "10px 14px", lineHeight: 1.6 }}>{success}</div>}
              {!success && (
                <button type="submit" className="lp-btn-primary"
                  disabled={loading || !hasSupabase || (!!confirmPassword && password !== confirmPassword)}
                  onMouseEnter={makeBig} onMouseLeave={makeSmall}>
                  {loading ? "LOADING..." : "UPDATE PASSWORD →"}
                </button>
              )}
            </form>
          )}

          {/* ── MAIN FORMS (signin / register / reset) ── */}
          {mode !== "update_password" && (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>

              {/* Google OAuth — at top for signin/register */}
              {mode !== "reset" && (
                <>
                  <button type="button" className="lp-btn-google"
                    onClick={signInWithGoogle} disabled={!hasSupabase}
                    onMouseEnter={makeBig} onMouseLeave={makeSmall}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Continue with Google
                  </button>

                  {/* OR divider */}
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ flex: 1, height: 1, background: BDR2 }} />
                    <span style={{ color: MUTED, fontSize: 9, letterSpacing: ".12em" }}>OR</span>
                    <div style={{ flex: 1, height: 1, background: BDR2 }} />
                  </div>
                </>
              )}

              {/* Email */}
              <div>
                <label style={{ display: "block", fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>EMAIL</label>
                <input type="email" required autoComplete="email"
                  className="lp-input" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={!hasSupabase || (mode === "reset" && !!success)}
                  onMouseEnter={makeBig} onMouseLeave={makeSmall} />
              </div>

              {/* Password */}
              {mode !== "reset" && (
                <div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <label style={{ fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase", color: MUTED }}>PASSWORD</label>
                    {mode === "signin" && (
                      <button type="button" className="lp-link"
                        onClick={() => switchMode("reset")}
                        onMouseEnter={makeBig} onMouseLeave={makeSmall}
                        style={{ fontSize: 9 }}>
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="lp-input-wrap">
                    <input type={showPass ? "text" : "password"} required
                      autoComplete={mode === "register" ? "new-password" : "current-password"}
                      className="lp-input" value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••" disabled={!hasSupabase}
                      onMouseEnter={makeBig} onMouseLeave={makeSmall} />
                    <button type="button" className="lp-eye" onClick={() => setShowPass(v => !v)}
                      onMouseEnter={makeBig} onMouseLeave={makeSmall} tabIndex={-1}>
                      {showPass ? "●" : "○"}
                    </button>
                  </div>
                </div>
              )}

              {/* Confirm password (register only) */}
              {mode === "register" && (
                <div>
                  <label style={{ display: "block", fontSize: 9, letterSpacing: ".12em", textTransform: "uppercase", color: MUTED, marginBottom: 8 }}>CONFIRM PASSWORD</label>
                  <div className="lp-input-wrap">
                    <input type={showConfirm ? "text" : "password"} required autoComplete="new-password"
                      className="lp-input" value={confirm} onChange={e => setConfirm(e.target.value)}
                      placeholder="••••••••" disabled={!hasSupabase}
                      style={{ borderColor: confirm && password !== confirm ? RED : BDR2 }}
                      onMouseEnter={makeBig} onMouseLeave={makeSmall} />
                    <button type="button" className="lp-eye" onClick={() => setShowConfirm(v => !v)}
                      onMouseEnter={makeBig} onMouseLeave={makeSmall} tabIndex={-1}>
                      {showConfirm ? "●" : "○"}
                    </button>
                  </div>
                  {confirm && password !== confirm && (
                    <div style={{ color: RED, fontSize: 9, marginTop: 6, letterSpacing: ".06em" }}>Passwords do not match</div>
                  )}
                </div>
              )}

              {/* Terms checkbox (register only) */}
              {mode === "register" && (
                <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer",
                  color: MUTED, fontSize: 10, lineHeight: 1.6, letterSpacing: ".04em" }}>
                  <input type="checkbox" checked={termsChecked} onChange={e => setTermsChecked(e.target.checked)}
                    style={{ marginTop: 3, accentColor: GREEN, flexShrink: 0 }} />
                  <span>
                    I agree to the{" "}
                    <button type="button" onClick={onOpenTerms} onMouseEnter={makeBig} onMouseLeave={makeSmall}
                      style={{ background: "none", border: "none", color: GREEN, cursor: "none", fontFamily: MONO, fontSize: 10, letterSpacing: ".04em", padding: 0 }}>
                      Terms of Service
                    </button>{" "}and{" "}
                    <button type="button" onClick={onOpenPrivacy} onMouseEnter={makeBig} onMouseLeave={makeSmall}
                      style={{ background: "none", border: "none", color: GREEN, cursor: "none", fontFamily: MONO, fontSize: 10, letterSpacing: ".04em", padding: 0 }}>
                      Privacy Policy
                    </button>
                  </span>
                </label>
              )}

              {/* Status messages */}
              {authError && <div style={{ color: RED, fontSize: 10, background: "#1a0800", border: `1px solid ${RED}22`, padding: "10px 14px", lineHeight: 1.6 }}>{authError}</div>}
              {success  && <div style={{ color: GREEN, fontSize: 10, background: FAINT, border: `1px solid ${GREEN}22`, padding: "10px 14px", lineHeight: 1.6 }}>{success}</div>}

              {/* Primary CTA */}
              {!(mode === "reset" && success) && (
                <button type="submit" className="lp-btn-primary"
                  disabled={
                    loading || !hasSupabase ||
                    (mode === "register" && (!termsChecked || (!!confirm && password !== confirm)))
                  }
                  onMouseEnter={makeBig} onMouseLeave={makeSmall}>
                  {loading ? "LOADING..."
                    : mode === "signin"  ? "Sign In →"
                    : mode === "reset"   ? "Send Reset Link →"
                    : "Create Account →"}
                </button>
              )}

              {/* Back to sign in (reset mode) */}
              {mode === "reset" && (
                <button type="button" className="lp-link"
                  onClick={() => switchMode("signin")}
                  onMouseEnter={makeBig} onMouseLeave={makeSmall}>
                  ← Back to sign in
                </button>
              )}
            </form>
          )}

          {/* Toggle link */}
          {mode !== "reset" && mode !== "update_password" && (
            <div style={{ marginTop: 28, fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: ".04em" }}>
              {mode === "signin" ? (
                <>Don't have an account?{" "}
                  <button className="lp-link lp-link-green"
                    onClick={() => switchMode("register")}
                    onMouseEnter={makeBig} onMouseLeave={makeSmall}>
                    Sign up →
                  </button>
                </>
              ) : (
                <>Already have an account?{" "}
                  <button className="lp-link lp-link-green"
                    onClick={() => switchMode("signin")}
                    onMouseEnter={makeBig} onMouseLeave={makeSmall}>
                    Sign in →
                  </button>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
